// First-run setup: create the very first operator.
// Only rendered when /api/auth/setup-status returns setupOpen=true.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { authApi } from "./api";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Logo } from "../../components/Logo";
import { useSetupStatus } from "./hooks";
import { ApiError } from "../../lib/api";

export function SetupScreen() {
  const setupStatus = useSetupStatus();
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const setup = useMutation({
    mutationFn: authApi.setup,
    onSuccess: () => {
      // After creating the first operator, refetch setup-status so the
      // app re-routes to login.
      setupStatus.refetch();
      window.location.reload(); // simplest: re-bootstraps the auth flow
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 403) {
        setError("El primer operador ya fue creado.");
      } else {
        setError("No se pudo crear el operador.");
      }
    },
  });

  if (setupStatus.isLoading) return null;
  if (setupStatus.data && !setupStatus.data.setupOpen) {
    // The app will route to login; nothing to render here.
    return null;
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return setError("Ingresá un nombre.");
    if (!/^\d{4,6}$/.test(pin)) return setError("El PIN debe tener 4 a 6 dígitos.");
    if (pin !== confirmPin) return setError("Los PINs no coinciden.");
    setup.mutate({ name: name.trim(), pin });
  };

  return (
    <main
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-6)",
        }}
      >
        <Logo height={44} />
        <h1
          style={{
            margin: 0,
            fontSize: "var(--font-size-22)",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          VIRO
        </h1>
        <p
          style={{
            margin: 0,
            color: "var(--text-secondary)",
            textAlign: "center",
            maxWidth: 320,
            fontSize: "var(--font-size-14)",
          }}
        >
          Creá el primer operador. Después podés agregar más desde el panel de admin.
        </p>

        <form
          onSubmit={submit}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-4)",
          }}
        >
          <Input
            label="Nombre del operador"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            autoFocus
            maxLength={60}
          />
          <Input
            label="PIN (4 a 6 dígitos)"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
          />
          <Input
            label="Repetir PIN"
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
          />
          {error && (
            <span
              style={{ color: "var(--color-danger)", fontSize: "var(--font-size-14)" }}
              role="alert"
            >
              {error}
            </span>
          )}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            disabled={setup.isPending}
          >
            {setup.isPending ? "Creando..." : "Crear operador"}
          </Button>
        </form>
      </div>
    </main>
  );
}